import type { Ops } from '@willie/schemas'

export type DataSourceMaker = 'shayp' | 'efento' | 'adeunis'

export const SUPPORTED_DATA_SOURCE_MAKERS: DataSourceMaker[] = ['shayp', 'efento', 'adeunis']

export const isSupportedDataSourceMaker = (maker: string | null | undefined): maker is DataSourceMaker =>
  maker === 'shayp' || maker === 'efento' || maker === 'adeunis'

export interface MeterDataSourceFieldValues {
  maker: DataSourceMaker
  // Serial number for shayp (resolved to a device UUID) or the device ID for efento/adeunis.
  serial: string
  channelAB: 'A' | 'B'
  channelNum: string
  pulseWeight: string
  // Only used for shayp (sent to Shayp's installation create endpoint).
  buildingType: Ops.StationInstallations.ShaypBuildingType
  // Only used for shayp: the device description sent when provisioning the device.
  description: string
}

// Shayp device description default: `orgName_installationID` (missing org dropped).
export const buildDeviceDescription = (orgName: string | null, installationId: number): string =>
  [orgName, String(installationId)].filter(Boolean).join('_')

export const defaultDataSourceValues = (
  maker: DataSourceMaker,
  serial: string,
  pulseWeight?: number | null,
  description = ''
): MeterDataSourceFieldValues => ({
  maker,
  serial,
  channelAB: 'A',
  channelNum: '1',
  pulseWeight: String(pulseWeight ?? (maker === 'efento' ? 1 : 0)),
  buildingType: 'OTHER',
  description
})

// Shayp building types grouped for the selector, with their French labels.
export const SHAYP_BUILDING_TYPE_GROUPS: {
  label: string
  options: { value: Ops.StationInstallations.ShaypBuildingType; label: string }[]
}[] = [
  {
    label: 'Logement',
    options: [
      { value: 'LIVING_HOUSEHOLD', label: 'Maison ou appartement simple' },
      { value: 'LIVING_APT_BUILD', label: 'Appartement' },
      { value: 'LIVING_STU_BUILD', label: 'Logement étudiant' },
      { value: 'LIVING_HOSTING_SMALL', label: 'Petit hôtel' },
      { value: 'LIVING_HOSTING_LARGE', label: 'Grand hôtel' },
      { value: 'LIVING_HOSPITAL', label: 'Hôpital' },
      { value: 'LIVING_NURSING', label: 'Maison de repos' }
    ]
  },
  {
    label: 'Lieu de travail',
    options: [
      { value: 'WORK_OFFICES', label: 'Bureaux' },
      { value: 'WORK_SCHOOL', label: 'École ou université' },
      { value: 'WORK_PUBLIC', label: 'Infrastructure publique' }
    ]
  },
  {
    label: 'Divertissement',
    options: [
      { value: 'FACILITY_SWIMM', label: 'Piscine' },
      { value: 'FACILITY_SPORT', label: 'Installation sportive' },
      { value: 'FACILITY_OTHER', label: 'Multi-usages' }
    ]
  },
  {
    label: 'Infrastructure',
    options: [
      { value: 'INFRA', label: 'Système de canalisations souterraines' },
      { value: 'FOUNTAIN', label: 'Fontaine' },
      { value: 'HIGHWAY_GAS_STATION', label: 'Station Service Autoroutière' },
      { value: 'OTHER', label: 'Autre' }
    ]
  }
]

// Seeds the data-source form from an installation's logger maker/serial/pulse weight,
// falling back to shayp when the logger isn't one we can auto-create.
export const initialDataSourceValues = (
  loggerMaker: string | null,
  serial: string | null,
  pulseWeight?: number | null,
  description = ''
): MeterDataSourceFieldValues =>
  defaultDataSourceValues(
    isSupportedDataSourceMaker(loggerMaker) ? loggerMaker : 'shayp',
    serial ?? '',
    pulseWeight,
    description
  )

export const validateDataSourceValues = (values: MeterDataSourceFieldValues): string | null => {
  if (!values.serial.trim())
    return values.maker === 'shayp' ? 'Serial number is required' : 'Device ID is required'
  if (values.maker === 'shayp' && !(Number(values.pulseWeight) > 0)) return 'Pulse weight must be greater than 0'
  if (values.maker === 'efento') {
    const channel = Number(values.channelNum)
    if (!Number.isInteger(channel) || channel <= 0) return 'Channel must be a positive number'
    if (!(Number(values.pulseWeight) > 0)) return 'Pulse weight must be greater than 0'
  }
  if (values.maker === 'adeunis' && Number(values.pulseWeight) < 0) return 'Pulse weight cannot be negative'
  return null
}

export const buildDataSourceInput = (
  values: MeterDataSourceFieldValues,
  index?: Ops.StationInstallations.CreateIndexReadingInput
): Ops.StationInstallations.CreateDataSourceInput => {
  const serial = values.serial.trim()
  if (values.maker === 'shayp') {
    // pulseWeight comes from the installation's hardware section (seeded via
    // initialDataSourceValues), falling back to 1. index is optional; the server
    // defaults it to 1 / now when omitted.
    return {
      maker: 'shayp',
      serialNumber: serial,
      pulseWeight: Number(values.pulseWeight) || 1,
      buildingType: values.buildingType,
      deviceDescription: values.description.trim() || undefined,
      index,
      readingMethod: 'incremental',
      unit: 'l'
    }
  }
  if (values.maker === 'adeunis') {
    return {
      maker: 'adeunis',
      deviceID: serial,
      channel: values.channelAB,
      pulseWeight: Number(values.pulseWeight) || 0,
      readingMethod: 'cumulative',
      unit: 'l'
    }
  }
  return {
    maker: 'efento',
    deviceID: serial,
    channel: Number(values.channelNum),
    pulseWeight: Number(values.pulseWeight) || 1,
    readingMethod: 'incremental',
    unit: 'l'
  }
}

// Informational note when a data source was created against an already-registered Shayp
// device: the create was skipped, so the operator's building type / description were not
// pushed to Shayp. Returns null when the device was newly created or the maker isn't shayp.
export const dataSourceReuseWarning = (result: {
  dataSourceStatus: Ops.StationInstallations.DataSourceStatus
  reusedExistingDevice: boolean
}): string | null =>
  result.dataSourceStatus === 'created' && result.reusedExistingDevice
    ? 'Data source created. This Shayp device was already registered, so the building type and description you selected were not applied to it in Shayp.'
    : null

// Returns a warning to show when the data source couldn't be created, or null when it
// succeeded / wasn't requested. Only shayp can hit device-not-found / no-connection.
export const dataSourceStatusWarning = (
  status: Ops.StationInstallations.DataSourceStatus,
  values: MeterDataSourceFieldValues
): string | null => {
  switch (status) {
    case 'device-not-found':
      return `We registered the Shayp installation for serial "${values.serial.trim()}", but the device isn't available in Shayp yet. Wait a moment, then retry from "Add data source".`
    case 'no-shayp-connection':
      return 'This organization is not connected to Shayp, so the data source could not be created.'
    case 'error':
      return 'The data source could not be created. You can retry from "Add data source".'
    default:
      return null
  }
}
